import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import {
  CREDIT_EXAMPLES, CREDIT_PACK, PLAN_RANK, checkoutErrorText, displayCurrency, formatMoney, planFeatures, priceParams, pricingFaqKeys, TIER_FREE, usePlanCatalog,
} from '../data/pricing';
import { Check, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { billingApi } from '../services/api';

const PricingPage = () => {
  const { t, i18n } = useTranslation('landing');
  const { isAuthenticated, user } = useAuth();
  const currency = displayCurrency(user);
  const pricingPlans = usePlanCatalog(currency);
  const prices = priceParams(pricingPlans, currency, i18n.language);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const navigate = useNavigate();

  const pricingFAQs = useMemo(
    () => pricingFaqKeys.map((key) => ({
      question: t(`pricing.faq.items.${key}.q`),
      answer: t(`pricing.faq.items.${key}.a`, prices),
    })),
    [t, prices]
  );

  // A signed-in person sees their own plan; the trial is offered only to who never had a subscription
  // (the backend gives it only then), a payer changes plans (Stripe's page, no second subscription).
  const signedIn = isAuthenticated && !!user;
  const currentPlan = signedIn ? (user.plan || TIER_FREE) : null;
  const trialOffered = !signedIn || ((!user.subscription_status || user.subscription_status === 'none') && currentPlan === TIER_FREE);
  const ctaLabel = (plan) => {
    if (!signedIn || (plan.id !== TIER_FREE && trialOffered && plan.id !== currentPlan)) return t(`pricing.plans.${plan.id}.cta`);
    if (plan.id === currentPlan) return t('subscription.current_plan', { ns: 'common' });
    const name = t(`pricing.plans.${plan.id}.name`);
    return t((PLAN_RANK[plan.id] ?? 0) > (PLAN_RANK[currentPlan] ?? 0) ? 'subscription.upgrade_to' : 'subscription.switch_to',
      { ns: 'common', plan: name });
  };

  const handleSelectPlan = async (plan) => {
    if (plan.id === TIER_FREE) {
      // A payer goes down to Free by cancelling on the subscription page.
      navigate(!isAuthenticated ? '/register' : currentPlan !== TIER_FREE ? '/app/subscription' : '/app');
      return;
    }

    if (!isAuthenticated) {
      navigate(`/register?plan=${plan.id}&interval=${billingCycle === 'yearly' ? 'year' : 'month'}`);
      return;
    }

    try {
      setLoadingPlan(plan.id);
      const { data } = await billingApi.createCheckoutSession({
        tier: plan.id,
        // BG_43: the backend CheckoutRequest expects interval as
        // Literal["monthly", "yearly"]; sending "month"/"year" caused a 422
        // and checkout could never start from the public pricing page.
        interval: billingCycle,
        seats: plan.minSeats,
        currency,
        lang: i18n.language,
        success_path: '/app/subscription?checkout=success',
        cancel_path: '/pricing?checkout=cancelled',
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error(t('pricing.checkout_failed'));
      }
    } catch (err) {
      toast.error(checkoutErrorText(t, err?.response?.data?.detail) || t('pricing.checkout_failed'));
    } finally {
      setLoadingPlan(null);
    }
  };

  const formatPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;
    if (price === 0) return t(`pricing.plans.${plan.id}.price_label_free`);
    return formatMoney(price, currency, i18n.language);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('pricing.seo.title')}
        description={t('pricing.seo.description', prices)}
        canonical="/pricing"
        faqItems={pricingFAQs}
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('pricing.breadcrumb_pricing') }
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: t('pricing.jsonld_name'),
          description: t('pricing.jsonld_desc'),
        }}
      />
      <Navbar />

      <main className="pt-20 pb-24">
        <article className="max-w-7xl mx-auto px-4">
          <header className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
              {t('pricing.headline')}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>

            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('pricing.billing.monthly')}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-black'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('pricing.billing.yearly')}
                <span className="ml-2 text-green-400 text-xs">{t('pricing.billing.save_20')}</span>
              </button>
            </div>
          </header>

          <section aria-labelledby="plans-heading" className="mb-24">
            <h2 id="plans-heading" className="sr-only">{t('pricing.plans_aria')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {pricingPlans.map((plan) => {
              const isLoading = loadingPlan === plan.id;
              const features = planFeatures(t, plan.id, plan.limits, i18n.language);
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-6 ${
                    plan.highlight
                      ? 'bg-gradient-to-b from-blue-900/50 to-gray-900 border-2 border-blue-500'
                      : 'bg-white/5 border border-gray-800'
                  }`}
                >
                  {plan.hasBadge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                        {t(`pricing.plans.${plan.id}.badge`)}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className="text-white text-xl font-semibold mb-2">{t(`pricing.plans.${plan.id}.name`)}</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-white">
                        {formatPrice(plan)}
                      </span>
                      <span className="text-gray-400 text-sm">{t(`pricing.plans.${plan.id}.period`)}</span>
                    </div>
                    {billingCycle === 'yearly' && plan.priceYearly > 0 && (
                      <p className="text-gray-500 text-xs mt-1">
                        {t(plan.perSeat ? 'pricing.billed_annually_member' : 'pricing.billed_annually', { amount: formatMoney(plan.priceYearly * 12, currency, i18n.language) })}
                      </p>
                    )}
                    {plan.perSeat && (
                      <p className="text-gray-500 text-xs mt-1">{t('pricing.features.min_members', { count: plan.minSeats })}</p>
                    )}
                    <p className="text-gray-400 text-sm mt-2">{t(`pricing.plans.${plan.id}.description`)}</p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {(Array.isArray(features) ? features : []).map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isLoading || plan.id === currentPlan}
                    className={`w-full py-3 rounded-lg font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                      plan.highlight
                        ? 'bg-white text-black hover:bg-gray-100'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {isLoading ? t('pricing.redirecting') : ctaLabel(plan)}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-gray-500 text-sm text-center mt-6">{t('pricing.vat_note')}</p>
          </section>

          <section aria-labelledby="credits-heading" className="max-w-3xl mx-auto mb-24">
            <h2 id="credits-heading" className="text-3xl font-bold text-white text-center mb-3">
              {t('pricing.credits.title')}
            </h2>
            <p className="text-gray-400 text-center mb-8">{t('pricing.credits.intro')}</p>
            <div className="rounded-2xl border border-gray-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-gray-400">
                  <tr>
                    <th className="text-left font-medium px-5 py-3">{t('pricing.credits.col_work')}</th>
                    <th className="text-right font-medium px-5 py-3">{t('pricing.credits.col_credits')}</th>
                  </tr>
                </thead>
                <tbody>
                  {CREDIT_EXAMPLES.map((row) => (
                    <tr key={row.key} className="border-t border-gray-800">
                      <td className="px-5 py-3 text-gray-300">{t(`pricing.credits.rows.${row.key}`)}</td>
                      <td className="px-5 py-3 text-right text-white tabular-nums">
                        {t('pricing.credits.about', { credits: Number(row.credits).toLocaleString(i18n.language) })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-gray-500 text-xs text-center mt-4">
              {t('pricing.credits.note', { credits: CREDIT_PACK.credits.toLocaleString(i18n.language), price: formatMoney(CREDIT_PACK[currency], currency, i18n.language) })}
            </p>
          </section>

          <section aria-labelledby="faq-heading" className="max-w-3xl mx-auto">
            <h2 id="faq-heading" className="text-3xl font-bold text-white text-center mb-8">
              {t('pricing.faq.headline')}
            </h2>
            <div className="space-y-4">
              {pricingFAQs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-800 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="text-white font-medium">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        openFAQ === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFAQ === index && (
                    <div className="px-6 pb-6 text-gray-400">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
